import type { Dispatch, ReactNode, SetStateAction } from "react";
import { Fragment } from "react";
import type {
  ExamFormTestTable,
  ExamFormTableColumnAnswer,
  ExamForm,
} from "@rmh/defs";
import SubInput from "../inputs/SubInput";
import AdditionalCommentsInput from "../inputs/AdditionalCommentsInput";
import TestFormContainer from "../inputs/TestFormContainer";
import { getExamFormAnswer } from "../helpers/getExamFormAnswer";

interface DisplayTableProps {
  item: ExamFormTestTable;
  examId: string;
  onInputChange?: () => void;
  setExamState?: Dispatch<SetStateAction<ExamForm[]>>;
  statusComponent: ReactNode;
  disabled: boolean;
}

function DisplayTable({
  item,
  onInputChange,
  setExamState,
  examId,
  statusComponent,
  disabled,
}: DisplayTableProps){
  const coloredRow = "bg-rmh-blue-50";
  const clearRow = "bg-white";

  const getDoubleArrayValue = (input: ExamFormTableColumnAnswer[][], column: number, index: number) => {
    if (!Array.isArray(input) || !Array.isArray(input[column])) {
      return "";
    }
    return input[column][index]?.inputValueComment ?? "";
  }

  const onChange =
    (subType: string, column: number, index: number) =>
    (...values: unknown[]) => {
      if (setExamState) {
        setExamState((examsMaster) => {
          const examToUpdate = examsMaster.find(
            (e) => e.examFormId === examId
          ) as ExamForm;
          const examToUpdateIndex = examsMaster.findIndex(
            (e) => e.examFormId === examId
          );
          const testToUpdate = examToUpdate.examFormTests.find(
            (e) => e.testTitle === item.testTitle
          ) as ExamFormTestTable;
          const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
            (e) => e.testTitle === item.testTitle
          );

          if (!Array.isArray(testToUpdate.examFormTableColumnAnswers)) {
            testToUpdate.examFormTableColumnAnswers = new Array(
              2
            ) as ExamFormTableColumnAnswer[][];
          }
          if (!Array.isArray(testToUpdate.examFormTableColumnAnswers[column])) {
            testToUpdate.examFormTableColumnAnswers[column] = new Array(
              item.examFormTableRows?.length
            ) as ExamFormTableColumnAnswer[];
          }
          (testToUpdate.examFormTableColumnAnswers[column] ?? [])[index] =
            getExamFormAnswer(subType, values);

          examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
          examsMaster[examToUpdateIndex] = examToUpdate;
          return [...examsMaster];
        });
      }
      if (onInputChange) {
        onInputChange();
      }
    };

  const handleAddInputChange =
    (column: number, index: number) =>
    (value: string): void => {
      if (setExamState) {
        setExamState((examsMaster) => {
          const examToUpdate = examsMaster.find(
            (e) => e.examFormId === examId
          ) as ExamForm;
          const examToUpdateIndex = examsMaster.findIndex(
            (e) => e.examFormId === examId
          );
          const testToUpdate = examToUpdate.examFormTests.find(
            (e) => e.testTitle === item.testTitle
          ) as ExamFormTestTable;
          const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
            (e) => e.testTitle === item.testTitle
          );
          if (!Array.isArray(testToUpdate.examFormTableColumnAnswers)) {
            testToUpdate.examFormTableColumnAnswers = new Array(2);
          }
          let rowsArray = testToUpdate.examFormTableColumnAnswers[column];
          if (!Array.isArray(rowsArray)) {
            rowsArray = new Array(item.examFormTableRows?.length);
          }
          if (typeof rowsArray[index] === "object") {
            (rowsArray as ExamFormTableColumnAnswer[])[index].inputValueComment = value;
          } else {
            rowsArray[index] = { inputValueComment: value };
          }
          examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
          examsMaster[examToUpdateIndex] = examToUpdate;
          return [...examsMaster];
        });
      }
      if (onInputChange) {
        onInputChange();
      }
    };

  const handleCommentChange = (value: string): void => {
    if (setExamState) {
      setExamState((examsMaster) => {
        const examToUpdate = examsMaster.find(
          (e) => e.examFormId === examId
        ) as ExamForm;
        const examToUpdateIndex = examsMaster.findIndex(
          (e) => e.examFormId === examId
        );
        const testToUpdate = examToUpdate.examFormTests.find(
          (e) => e.testTitle === item.testTitle
        ) as ExamFormTestTable;
        const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
          (e) => e.testTitle === item.testTitle
        );
        testToUpdate.commentsText = value;
        examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
        examsMaster[examToUpdateIndex] = examToUpdate;
        return [...examsMaster];
      });
    }
    if (onInputChange) {
      onInputChange();
    }
  };

  return (
    <TestFormContainer
      statusSlot={statusComponent}
      title={item.testTitle ?? ""}
    >
      <div className="grid w-full grid-cols-3 divide-x divide-y break-all border-b border-r">
        <div className="flex-1  px-4 py-2 text-center text-lg font-semibold text-black" />
        <div
          className={`border-rmh-gray-300 flex-1 border border-b-0 border-r-0 px-4 py-2 text-center text-lg font-semibold text-black ${coloredRow}`}
        >
          Left
        </div>
        <div
          className={`border-rmh-gray-300 flex-1 border border-b-0 px-4 py-2 text-center text-lg font-semibold text-black ${coloredRow}`}
        >
          Right
        </div>
        {item.examFormTableRows?.map((row, index) => {
          const defaultValues = item.examFormTableColumnAnswers as
            | ExamFormTableColumnAnswer[][]
            | undefined;
          const leftDefaultArray =
            defaultValues && defaultValues.length > 0
              ? defaultValues[0]
              : undefined;
          const leftDefault =
            leftDefaultArray && leftDefaultArray.length > 0
              ? leftDefaultArray[index]
              : undefined;
          const rightDefaultArray =
            defaultValues && defaultValues.length > 0
              ? defaultValues[1]
              : undefined;
          const rightDefault =
            rightDefaultArray && rightDefaultArray.length > 0
              ? rightDefaultArray[index]
              : undefined;
          return (
            <Fragment key={`tablerow_${row.id}`}>
              <div
                className={`flex  items-center p-4 font-semibold text-black ${
                  index % 2 === 0 ? clearRow : coloredRow
                }`}
              >
                {(row.examFormTableRowType as string) ===
                  "MultipleSelectDropdown" ||
                (row.examFormTableRowType as string) === "SingleSelectDropdown"
                  ? row.rowTitle?.split(";")[0]
                  : row.rowTitle}
              </div>
              <div
                className={`flex w-full flex-col p-4 ${
                  index % 2 === 0 ? clearRow : coloredRow
                }`}
              >
                <SubInput
                  answers={leftDefault}
                  disabled={disabled}
                  onInputChange={onChange(row.examFormTableRowType, 0, index)}
                  row={row}
                  subInputType={row.examFormTableRowType}
                />
                {Boolean(row.addInputText) && (
                  <AdditionalCommentsInput
                    defaultValue={getDoubleArrayValue(item.examFormTableColumnAnswers as ExamFormTableColumnAnswer[][], 0, index)}
                    disabled={disabled}
                    onChange={handleAddInputChange(0, index)}
                    placeholder="Comments"
                  />
                )}
              </div>
              <div
                className={`flex w-full flex-col p-4 ${
                  index % 2 === 0 ? clearRow : coloredRow
                }`}
              >
                <SubInput
                  answers={rightDefault}
                  disabled={disabled}
                  onInputChange={onChange(row.examFormTableRowType, 1, index)}
                  row={row}
                  subInputType={row.examFormTableRowType}
                />
                {Boolean(row.addInputText) && (
                  <AdditionalCommentsInput
                    defaultValue={getDoubleArrayValue(item.examFormTableColumnAnswers as ExamFormTableColumnAnswer[][], 1, index)}
                    disabled={disabled}
                    onChange={handleAddInputChange(1, index)}
                    placeholder="Comments"
                  />
                )}
              </div>
            </Fragment>
          );
        })}
      </div>
      {Boolean(item.comments) && (
        <AdditionalCommentsInput
          defaultValue={item.commentsText ?? ""}
          disabled={disabled}
          onChange={handleCommentChange}
        />
      )}
    </TestFormContainer>
  );
}

export default DisplayTable;
